import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Clock, Calendar, Users, Repeat, Sparkles, ShieldAlert, FileText, Zap,
  CheckCircle2, Pause, UserPlus, MoreHorizontal, Pencil, Trash2,
  UtensilsCrossed, ListChecks, Wallet, ShoppingCart, Heart,
  GraduationCap, Car, Briefcase, Home as HomeIcon, HeartHandshake,
} from "lucide-react";
import { householdMembers } from "../data/homeOsMockData";
import { SwapTaskModal } from "./SwapTaskModal";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { suggestMembersForTask } from "../utils/choreIntelligence";

/* ════════════════════════════════════════════════════════════════════════
   HomeOSTaskCard — one task, friendly + readable
   Drives all UI off a single `task` object from `householdTasks`.
   Action buttons use local state + toast for now (no backend yet).
   ════════════════════════════════════════════════════════════════════════ */

const STATUS_STYLES = {
  "Upcoming":       { pill: "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-blue-700/60",         dot: "bg-transparent" },
  "Needs action":   { pill: "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-amber-700/60",   dot: "bg-transparent" },
  "Ara suggested":   { pill: "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-violet-700/60", dot: "bg-transparent" },
  "Done":           { pill: "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-emerald-700/60", dot: "bg-transparent" },
  "Paused":         { pill: "bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-slate-700",            dot: "bg-gray-400" },
};

const PRIORITY_STYLES = {
  low:    "bg-gray-50 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-slate-700",
  medium: "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-blue-800",
  high:   "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-orange-800",
  urgent: "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-rose-700 ring-1 ring-rose-200 dark:ring-rose-700/40",
};

const CATEGORY_ICONS = {
  "Meals":     UtensilsCrossed,
  "Chores":    ListChecks,
  "Calendar":  Calendar,
  "Money":     Wallet,
  "Supplies":  ShoppingCart,
  "Health":    Heart,
  "Documents": FileText,
  "Transport": Car,
  "School":    GraduationCap,
  "Family":    Users,
  "Admin":     Briefcase,
};

/* Map task.assignedTo (id or special token) → display name. */
function resolveAssigneeFrom(members, assignedTo) {
  if (!assignedTo) return "Unassigned";
  if (assignedTo === "all") return "Everyone";
  if (assignedTo === "ara") return "ARA";
  const member = (members || []).find(m => m.id === assignedTo) || householdMembers.find(m => m.id === assignedTo);
  return member?.name || assignedTo;
}

/* Format due date+time as a single friendly chunk. */
function formatDue(dueDate, dueTime) {
  if (!dueDate && !dueTime) return null;
  let datePart = "";
  if (dueDate) {
    try {
      const d = new Date(dueDate + "T00:00:00");
      datePart = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    } catch {
      datePart = dueDate;
    }
  }
  return dueTime ? `${datePart} · ${dueTime}` : datePart;
}

export function HomeOSTaskCard({ task, onAction, onEdit, onDelete, onMarkDone, onPause }) {
  const [localStatus, setLocalStatus] = useState(task.status);
  const [swapOpen, setSwapOpen] = useState(false);
  const { members, tasks, updateTask } = useHomeOSState();
  const status = STATUS_STYLES[localStatus] || STATUS_STYLES["Upcoming"];
  const priority = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium;
  const CategoryIcon = CATEGORY_ICONS[task.category] || HomeIcon;
  const due = formatDue(task.dueDate, task.dueTime);
  const assignee = resolveAssigneeFrom(members, task.assignedTo);
  const isDone = localStatus === "Done";
  const isPaused = localStatus === "Paused";

  /* ARA's quiet note for unassigned tasks — derived, never persisted. */
  const suggestion = useMemo(() => {
    if (task.assignedTo || isDone || isPaused) return null;
    const s = suggestMembersForTask(task, members, tasks);
    return s.suggestions.length > 0 ? s : null;
  }, [task, members, tasks, isDone, isPaused]);

  const handle = (key, label, nextStatus) => {
    if (nextStatus) setLocalStatus(nextStatus);
    toast(`${label} — ${task.title}`);
    // eslint-disable-next-line no-console
    console.log("[HomeOS task action]", key, task);
    onAction?.(task, key);
    // Bubble specific intents to the parent so it can update shared state
    if (key === "done" && onMarkDone) onMarkDone(task);
    if (key === "pause" && onPause)   onPause(task, isPaused ? "Upcoming" : "Paused");
  };

  return (
    <div
      className={`rounded-2xl border bg-white dark:bg-slate-900/60 border-gray-200 dark:border-slate-700 p-4 shadow-sm hover:shadow-md transition-shadow ${
        isDone ? "opacity-70" : isPaused ? "opacity-60" : ""
      }`}
      data-testid={`task-card-${task.id}`}
    >
      {/* Header: status dot + title */}
      <div className="flex items-start gap-2.5 mb-2">
        <span className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${status.dot}`} />
        <div className="flex-1 min-w-0">
          <h4
            className={`text-sm font-extrabold leading-snug ${
              isDone ? "line-through text-gray-500 dark:text-gray-400" : "text-gray-900 dark:text-gray-50"
            }`}
            data-testid={`task-title-${task.id}`}
          >
            {task.title}
          </h4>
          {task.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug line-clamp-2">{task.description}</p>
          )}
        </div>
      </div>

      {/* Pills row */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-extrabold uppercase tracking-wide ${status.pill}`}
          data-testid={`task-status-${task.id}`}
        >
          {localStatus}
        </span>

        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-extrabold uppercase tracking-wide ${priority}`}
          data-testid={`task-priority-${task.id}`}
        >
          <Zap className="w-2.5 h-2.5" strokeWidth={1.5} />
          {task.priority || "medium"}
        </span>

        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-[10px] font-extrabold uppercase tracking-wide text-gray-600 dark:text-gray-300"
          data-testid={`task-category-${task.id}`}
        >
          <CategoryIcon className="w-2.5 h-2.5" strokeWidth={1.5} />
          {task.category}
        </span>

        {task.aiSuggested && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[color:var(--ara-line-soft)] dark:border-violet-700/60 bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] text-[10px] font-extrabold uppercase tracking-wide"
            data-testid={`task-ai-badge-${task.id}`}
          >
            <Sparkles className="w-2.5 h-2.5" strokeWidth={1.5} />
            Ara suggested
          </span>
        )}

        {task.approvalRequired && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[color:var(--ara-line-soft)] dark:border-amber-700/60 bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] text-[10px] font-extrabold uppercase tracking-wide"
            data-testid={`task-approval-badge-${task.id}`}
          >
            <ShieldAlert className="w-2.5 h-2.5" strokeWidth={1.5} />
            Needs approval
          </span>
        )}

        {task.swappedAt && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[color:var(--ara-line-soft)] dark:border-pink-700/60 bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] text-[10px] font-extrabold uppercase tracking-wide"
            data-testid={`task-swapped-badge-${task.id}`}
            title={task.swapReason ? `Reason: ${task.swapReason}` : "Swapped kindly"}
          >
            <HeartHandshake className="w-2.5 h-2.5" strokeWidth={1.5} />
            Swapped
          </span>
        )}
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400 mb-3">
        <span className="inline-flex items-center gap-1 font-bold text-gray-700 dark:text-gray-200">
          <Users className="w-3 h-3" strokeWidth={1.5} />
          {assignee}
        </span>
        {due && (
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" strokeWidth={1.5} />
            {due}
          </span>
        )}
        {task.recurrence && (
          <span className="inline-flex items-center gap-1">
            <Repeat className="w-3 h-3" strokeWidth={1.5} />
            {task.recurrence}
          </span>
        )}
        {task.energyRequired && (
          <span className="inline-flex items-center gap-1">
            <Zap className="w-3 h-3" strokeWidth={1.5} />
            {task.energyRequired} energy
          </span>
        )}
        {task.source && (
          <span className="text-[10px] italic text-gray-400 dark:text-gray-500">via {task.source}</span>
        )}
      </div>

      {/* ARA's quiet suggestion — the resident decides */}
      {suggestion && (
        <div className="mb-3" data-testid={`task-ara-suggestion-${task.id}`}>
          <p
            className="italic text-[12.5px] leading-[1.5]"
            style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink-muted)", fontWeight: 400 }}
          >
            {suggestion.line}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            {suggestion.suggestions.map(s => (
              <button
                key={s.memberId}
                type="button"
                onClick={() => { updateTask(task.id, { assignedTo: s.memberId }); toast(`Assigned to ${s.name}.`); }}
                className="text-[12px] min-h-[32px] transition-opacity hover:opacity-70"
                style={{ fontFamily: "Inter, system-ui, sans-serif", color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}
                aria-label={`Assign this task to ${s.name}`}
                data-testid={`task-ara-assign-${task.id}-${s.memberId}`}
              >
                Assign to {s.name} →
              </button>
            ))}
            <button
              type="button"
              onClick={() => onEdit && onEdit(task)}
              className="text-[12px] min-h-[32px] transition-opacity hover:opacity-70"
              style={{ fontFamily: "Inter, system-ui, sans-serif", color: "var(--ara-ink-soft)", fontWeight: 400, background: "none", border: "none", cursor: "pointer", padding: 0 }}
              aria-label="Choose someone else for this task"
              data-testid={`task-ara-choose-else-${task.id}`}
            >
              Choose someone else →
            </button>
          </div>
        </div>
      )}

      {/* Notes */}
      {task.notes && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 italic mb-3 leading-snug">
          “{task.notes}”
        </p>
      )}

      {task.swappedAt && task.previousAssignedTo && (
        <p className="text-[11px] text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mb-3 leading-snug" data-testid={`task-swap-note-${task.id}`}>
          Originally assigned to {resolveAssigneeFrom(members, task.previousAssignedTo)}. {resolveAssigneeFrom(members, task.assignedTo)} took this on.
          {task.swapReason && <span className="block italic text-[color:var(--ara-ink-muted)]/80 dark:text-[color:var(--ara-ink-muted)]/80">Reason: {task.swapReason}</span>}
        </p>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-1.5">
        <ActionBtn
          icon={CheckCircle2} label="Mark done"
          onClick={() => handle("done", "Marked done", "Done")}
          disabled={isDone}
          testId={`task-action-done-${task.id}`}
          variant="primary"
        />
        <ActionBtn
          icon={UserPlus} label="Reassign"
          onClick={() => onEdit ? onEdit(task) : handle("reassign", "Reassign requested")}
          testId={`task-action-reassign-${task.id}`}
        />
        {!isDone && (
          <ActionBtn
            icon={HeartHandshake} label="I'll take this"
            onClick={() => setSwapOpen(true)}
            testId={`task-action-swap-${task.id}`}
            variant="swap"
          />
        )}
        <ActionBtn
          icon={Pause} label={isPaused ? "Resume" : "Pause"}
          onClick={() => handle("pause", isPaused ? "Resumed" : "Paused", isPaused ? "Upcoming" : "Paused")}
          testId={`task-action-pause-${task.id}`}
          active={isPaused}
        />
        {onEdit && (
          <ActionBtn
            icon={Pencil} label="Edit"
            onClick={() => onEdit(task)}
            testId={`task-action-edit-${task.id}`}
          />
        )}
        {onDelete && (
          <ActionBtn
            icon={Trash2} label="Delete"
            onClick={() => onDelete(task)}
            testId={`task-action-delete-${task.id}`}
            variant="danger"
          />
        )}
        {!onEdit && !onDelete && (
          <ActionBtn
            icon={MoreHorizontal} label="Details"
            onClick={() => handle("details", "Details opening soon")}
            testId={`task-action-details-${task.id}`}
          />
        )}
      </div>

      <SwapTaskModal open={swapOpen} onClose={() => setSwapOpen(false)} task={task} taskKind="task" />
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, disabled, active, variant, testId }) {
  const base = "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border transition-all duration-150 active:scale-95";
  const stateClass = disabled
    ? "bg-gray-50 dark:bg-slate-800 text-gray-300 dark:text-slate-600 border-gray-100 dark:border-slate-700 cursor-not-allowed"
    : active
      ? "bg-[#1a4d3e] text-white border-[#1a4d3e]"
      : variant === "primary"
        ? "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-emerald-700/60 hover:bg-transparent dark:hover:opacity-80/50"
        : variant === "danger"
          ? "bg-white dark:bg-slate-800 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-rose-800 hover:bg-transparent dark:hover:opacity-80/30"
          : variant === "swap"
            ? "bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-pink-700/60 hover:bg-transparent dark:hover:opacity-80/40"
            : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-[color:var(--ara-line-soft)] dark:hover:border-violet-600 hover:text-[color:var(--ara-ink-muted)] dark:hover:text-[color:var(--ara-ink-muted)]";

  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${stateClass}`} data-testid={testId}>
      <Icon className="w-2.5 h-2.5" strokeWidth={1.5} />
      {label}
    </button>
  );
}

export default HomeOSTaskCard;
